import { type PermissionModule, crud } from './permission-defs';

/** The front of the lab: patients, reports and the catalogue they are built from. */
export const CLINICAL_MODULES = [
  {
    key: 'workspace',
    label: 'Workspace',
    description: 'The home screen and the worklist.',
    items: [
      {
        label: 'Dashboard',
        permissions: {
          'dashboard.view': { action: 'View', description: 'Open the dashboard.' },
        },
      },
      {
        label: 'Worklist',
        permissions: {
          'worklist.view': { action: 'View', description: 'Open the worklist of pending reports.' },
        },
      },
    ],
  },
  {
    key: 'patients',
    label: 'Patients',
    description: 'Registration, visits and payments at the front desk.',
    items: [
      {
        label: 'Patients',
        permissions: {
          'patient.view': {
            action: 'View',
            description: 'Search patients and open a patient record, its visits and trends.',
          },
          'patient.create': {
            action: 'Register',
            description: 'Register a new patient or visit.',
            requires: ['patient.view'],
          },
          'patient.edit': {
            action: 'Edit',
            description: 'Change a registered patient or their tests.',
            requires: ['patient.view'],
          },
          'patient.delete': {
            action: 'Delete',
            description: 'Delete a patient and everything recorded against them.',
            requires: ['patient.view'],
            sensitive: true,
          },
          'patient.payment': {
            action: 'Take payment',
            description: 'Record a payment or a correction against a visit.',
            requires: ['patient.view', 'billing.view'],
          },
        },
      },
      {
        label: 'Order requests',
        permissions: {
          'orderRequest.view': {
            action: 'View',
            description: 'See tests doctors have requested before registration.',
          },
          'orderRequest.create': {
            action: 'Create',
            description: 'Enter a new order request.',
            requires: ['orderRequest.view'],
          },
          'orderRequest.edit': {
            action: 'Edit',
            description: 'Change a pending order request.',
            requires: ['orderRequest.view'],
          },
          'orderRequest.register': {
            action: 'Register',
            description: 'Turn an order request into a patient visit.',
            requires: ['orderRequest.view', 'patient.create'],
          },
          'orderRequest.decline': {
            action: 'Decline / reopen',
            description: 'Decline an order request, or reopen a declined one.',
            requires: ['orderRequest.view'],
          },
          'orderRequest.delete': {
            action: 'Delete',
            description: 'Remove an order request.',
            requires: ['orderRequest.view'],
          },
        },
      },
      {
        label: 'Panels',
        permissions: crud('panel', 'panel'),
      },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    description: 'Entering, completing and printing results.',
    items: [
      {
        label: 'Reports',
        permissions: {
          'report.view': { action: 'View', description: 'Open a report and its history.' },
          'report.enterResults': {
            action: 'Enter results',
            description: 'Type results into an open report.',
            requires: ['report.view'],
          },
          'report.complete': {
            action: 'Complete',
            description: 'Mark a report as completed, which locks it.',
            requires: ['report.view', 'report.enterResults'],
          },
          'report.reopen': {
            action: 'Reopen',
            description: 'Unlock a completed report so it can be corrected.',
            requires: ['report.view', 'report.enterResults'],
            sensitive: true,
          },
          'report.print': {
            action: 'Print',
            description: 'Print or download a report.',
            requires: ['report.view'],
          },
        },
      },
      {
        label: 'WhatsApp',
        permissions: {
          'whatsapp.history': {
            action: 'View history',
            description: 'See what was already sent to one patient.',
          },
          'whatsapp.share': {
            action: 'Send',
            description: 'Send a finalized report to a patient on WhatsApp.',
            requires: ['whatsapp.history', 'report.view'],
          },
          'whatsapp.audit': {
            action: 'Audit log',
            description: 'The lab-wide log of every message sent, and the connection status.',
            sensitive: true,
          },
        },
      },
      {
        label: 'Print templates',
        permissions: {
          'printTemplate.manage': {
            action: 'Manage',
            description: 'Design, preview, set the default and delete print templates.',
          },
        },
      },
    ],
  },
  {
    key: 'catalogue',
    label: 'Catalogue',
    description: 'Tests, parameters and the lists reports are built from.',
    items: [
      { label: 'Lab tests', permissions: crud('labTest', 'lab test') },
      { label: 'Departments', permissions: crud('department', 'department') },
      { label: 'Consultants', permissions: crud('consultant', 'consultant') },
      { label: 'Test parameters', permissions: crud('testParameter', 'test parameter') },
      {
        label: 'Test procedures',
        permissions: {
          ...crud('procedure', 'test procedure'),
          'procedure.print': {
            action: 'Print',
            description: 'Print a test procedure (SOP).',
            requires: ['procedure.view'],
          },
        },
      },
      { label: 'Report comments', permissions: crud('reportComment', 'report comment') },
      { label: 'Report remarks', permissions: crud('reportRemark', 'report remark') },
      { label: 'Antibiotics', permissions: crud('antibiotic', 'antibiotic') },
      { label: 'Organisms', permissions: crud('organism', 'organism') },
      {
        label: 'Rate list',
        permissions: {
          'rateList.view': { action: 'View', description: 'See and print the rate list.' },
          'pricing.quote': {
            action: 'Price calculator',
            description: 'Quote a price for a set of tests.',
          },
        },
      },
    ],
  },
] as const satisfies readonly PermissionModule[];
